/**
 * ImageGalleryPage Component for LOOPINO AI Platform
 * Mobile page for viewing, uploading, and generating images
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './ImageGalleryPage.css';

import useApi from '../../../hooks/useApi';
import useLanguage from '../../../hooks/useLanguage';
import { useMobileSwipe } from '../../../hooks/useMobileSwipe';
import {
  DEFAULT_GALLERY_PAGE_SIZE,
  getSubtypeQueryValue,
  normalizeMediaItem as normalizeGalleryMediaItem,
  fetchAlbumMediaItems,
  generateVideoThumbnail,
} from './galleryUtils';

// Import placeholder image (used as fallback when a media item lacks a URL)
import placeholderImage from '../../../assets/images/placeholder-post-image.png';
import videoPlaceholder from '../../../assets/images/placeholder-video-thumb.svg';
import { Skeleton } from '../../effects';

const GalleryThumbnail = React.memo(({
  image,
  onSelect,
  getImageLoadingProps,
  imageLoadingScope,
  shouldLoadImage = true,
  onSequentialLoadComplete,
  thumbnailUrl,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const sequentialCompleteRef = useRef(false);
  const isVideo = image?.mediaType === 'video' || image?.raw?.media_type === 'video';

  const resolvedThumbnailUrl = thumbnailUrl || (!isVideo ? image?.url : null) || (isVideo ? videoPlaceholder : placeholderImage);
  const effectiveUrl = hasError ? placeholderImage : resolvedThumbnailUrl;
  const imageLoadingKey = thumbnailUrl
    ? `${image?.id || image?.url || image?.name || 'media'}-thumb`
    : (image?.id || image?.url || image?.name || effectiveUrl);
  const blurProps = useMemo(() => {
    if (typeof getImageLoadingProps !== 'function') {
      return null;
    }
    return getImageLoadingProps(imageLoadingKey, imageLoadingScope || 'gallery');
  }, [getImageLoadingProps, imageLoadingKey, imageLoadingScope]);

  const resolvedSourceUrl = useMemo(() => {
    const rawSource = image?.raw;
    const candidate = (
      rawSource?.public_url ||
      rawSource?.url ||
      rawSource?.file_url ||
      rawSource?.fileUrl ||
      image?.url ||
      null
    );
    if (candidate) {
      return candidate;
    }
    return isVideo ? null : effectiveUrl;
  }, [effectiveUrl, image, isVideo]);
  const displayUrl = isVideo ? (thumbnailUrl || videoPlaceholder) : effectiveUrl;

  useEffect(() => {
    sequentialCompleteRef.current = false;
  }, [imageLoadingKey]);

  const handleClick = useCallback(() => {
    if (typeof onSelect === 'function') {
      onSelect({
        ...image,
        displayUrl,
        selectedUrl: resolvedSourceUrl,
      });
    }
  }, [displayUrl, image, onSelect, resolvedSourceUrl]);

  const placeholderStyle = useMemo(() => ({
    backgroundImage: `url(${effectiveUrl})`,
  }), [effectiveUrl]);

  const notifySequentialComplete = useCallback(() => {
    if (sequentialCompleteRef.current) {
      return;
    }
    sequentialCompleteRef.current = true;
    if (typeof onSequentialLoadComplete === 'function') {
      onSequentialLoadComplete();
    }
  }, [onSequentialLoadComplete]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  }, [handleClick]);

  const handleLoad = useCallback((event) => {
    setIsLoaded(true);
    blurProps?.onLoad?.(event);
    notifySequentialComplete();
  }, [blurProps, notifySequentialComplete]);

  const handleVideoMetadata = useCallback((event) => {
    const video = event.currentTarget;
    if (!video || typeof video.currentTime !== 'number') {
      return;
    }
    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    const targetTime = duration > 0 ? Math.min(0.1, Math.max(0, duration - 0.1)) : 0.1;
    if (Number.isFinite(targetTime) && targetTime > 0 && video.currentTime !== targetTime) {
      try {
        video.currentTime = targetTime;
      } catch (error) {
        // Ignore seek failures for thumbnails.
      }
    }
  }, []);

  const handleError = useCallback((event) => {
    if (!hasError) {
      setHasError(true);
      setIsLoaded(false);
      blurProps?.onError?.(event);
      if (event.currentTarget?.tagName === 'IMG') {
        event.currentTarget.onerror = null;
        event.currentTarget.src = placeholderImage;
      }
      notifySequentialComplete();
      return;
    }

    blurProps?.onError?.(event);
    setIsLoaded(true);
    notifySequentialComplete();
  }, [blurProps, hasError, notifySequentialComplete]);

  const shouldRenderMedia = shouldLoadImage || isLoaded;
  const shouldRenderVideo = isVideo && !thumbnailUrl && !hasError && resolvedSourceUrl;

  return (
    <div
      className="image-gallery-item"
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div className={[
        'image-gallery-thumb',
        isLoaded ? 'loaded' : 'loading',
        shouldLoadImage ? '' : 'waiting'
      ].filter(Boolean).join(' ')}>
        <div
          className={`image-gallery-thumb-placeholder ${isLoaded ? 'hidden' : ''}`}
          style={placeholderStyle}
        >
          {!isLoaded && (
            <Skeleton variant="rounded" width="100%" height="100%" shimmer />
          )}
        </div>
        {shouldRenderMedia && (shouldRenderVideo ? (
          <video
            src={resolvedSourceUrl}
            poster={videoPlaceholder}
            className={[
              'image-gallery-item-img',
              isLoaded ? 'loaded' : 'loading',
              blurProps ? 'mobile-image-with-loading' : '',
              blurProps?.className || ''
            ].filter(Boolean).join(' ')}
            preload="auto"
            muted
            playsInline
            onLoadedData={handleLoad}
            onLoadedMetadata={handleVideoMetadata}
            onError={handleError}
          />
        ) : (
          <img
            src={hasError ? placeholderImage : effectiveUrl}
            alt={image.name}
            className={[
              'image-gallery-item-img',
              isLoaded ? 'loaded' : 'loading',
              blurProps ? 'mobile-image-with-loading' : '',
              blurProps?.className || ''
            ].filter(Boolean).join(' ')}
            loading="lazy"
            decoding="async"
            onLoad={handleLoad}
            onError={handleError}
          />
        ))}
      </div>
    </div>
  );
});

// Icons
const BackIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const VIEWER_ANIMATION_MS = 220;
const DEFAULT_MAX_PARALLEL_IMAGE_LOADS = 3;

const ImageGalleryPage = ({
  onBack,
  onImageSelect,
  viewerOrigin = 'post-edit',
  maxParallelImageLoads = DEFAULT_MAX_PARALLEL_IMAGE_LOADS,
}) => {
  const { get } = useApi();
  const { t } = useLanguage();
  const parallelLoadLimit = useMemo(() => {
    const candidate = Number(maxParallelImageLoads);
    if (Number.isFinite(candidate) && candidate >= 1) {
      return Math.floor(candidate);
    }
    return DEFAULT_MAX_PARALLEL_IMAGE_LOADS;
  }, [maxParallelImageLoads]);

  // State management
  const [selectedType, setSelectedType] = useState('all'); // 'all', 'uploaded', 'generated', 'edited'
  const [selectedMediaType, setSelectedMediaType] = useState('all'); // 'all', 'image', 'video'
  const [selectedAlbum, setSelectedAlbum] = useState('all'); // 'all' or album id
  const [isLoading, setIsLoading] = useState(false);
  const [mediaItems, setMediaItems] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [galleryError, setGalleryError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, totalItems: 0 });
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [imageLoadingStates, setImageLoadingStates] = useState(() => new Map());
  const [videoThumbnails, setVideoThumbnails] = useState({});
  const [videoThumbnailsLoading, setVideoThumbnailsLoading] = useState({});
  const [viewerImage, setViewerImage] = useState(null);
  const [isViewerVisible, setIsViewerVisible] = useState(false);
  const [sequentialLoadCursor, setSequentialLoadCursor] = useState(() => Math.max(0, parallelLoadLimit - 1));

  const setImageLoadingState = useCallback((key, status) => {
    if (!key) {
      return;
    }

    setImageLoadingStates((prev) => {
      const current = prev.get(key);
      if (current === status) {
        return prev;
      }
      const next = new Map(prev);
      next.set(key, status);
      return next;
    });
  }, []);

  const getImageLoadingProps = useCallback((source, scope = 'gallery') => {
    const normalizedScope = scope || 'gallery';
    const baseKey = typeof source === 'string' && source.length > 0
      ? source
      : 'unknown';
    const mapKey = `${normalizedScope}::${baseKey}`;
    const status = imageLoadingStates.get(mapKey) || 'loading';

    const statusClass = status === 'loaded'
      ? 'is-ready'
      : status === 'error'
        ? 'is-error'
        : 'is-loading';

    return {
      status,
      className: statusClass,
      onLoad: () => setImageLoadingState(mapKey, 'loaded'),
      onError: () => setImageLoadingState(mapKey, 'error')
    };
  }, [imageLoadingStates, setImageLoadingState]);

  // Dropdown state
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
  const [isMediaDropdownOpen, setIsMediaDropdownOpen] = useState(false);
  const [isAlbumDropdownOpen, setIsAlbumDropdownOpen] = useState(false);

  // Refs
  const typeDropdownRef = useRef(null);
  const mediaDropdownRef = useRef(null);
  const albumDropdownRef = useRef(null);
  const albumMediaCacheRef = useRef(new Map());
  const loadMoreTriggerRef = useRef(null);
  const activeRequestRef = useRef(null);
  const swipeEdgeBlockRef = useRef(false);
  const pageRef = useRef(null);
  const sequentialScopeRef = useRef(null);
  const overlayRef = useRef(null);
  const viewerHideTimerRef = useRef(null);

  const [swipeOffset, setSwipeOffset] = useState(0);
  const normalizedViewerOrigin = viewerOrigin || 'post-edit';
  const isAiStorageView = normalizedViewerOrigin === 'ai-storage';
  const showViewerSelectButton = !isAiStorageView && typeof onImageSelect === 'function';

  const normalizeMediaItem = useCallback((item) => {
    return normalizeGalleryMediaItem(item, t);
  }, [t]);

  const resolveMediaSourceUrl = useCallback((item) => {
    if (!item) {
      return null;
    }
    const rawSource = item.raw || {};
    return (
      rawSource.public_url ||
      rawSource.url ||
      rawSource.file_url ||
      rawSource.fileUrl ||
      item.url ||
      null
    );
  }, []);

  const resolveThumbnailKey = useCallback((item) => {
    const rawKey = item?.id || resolveMediaSourceUrl(item);
    return rawKey ? `${rawKey}` : null;
  }, [resolveMediaSourceUrl]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target)) {
        setIsTypeDropdownOpen(false);
      }
      if (mediaDropdownRef.current && !mediaDropdownRef.current.contains(event.target)) {
        setIsMediaDropdownOpen(false);
      }
      if (albumDropdownRef.current && !albumDropdownRef.current.contains(event.target)) {
        setIsAlbumDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch available albums on mount
  useEffect(() => {
    let isCancelled = false;

    const loadAlbums = async () => {
      try {
        const params = new URLSearchParams({
          page: '1',
          page_size: '50',
          include_archived: 'false',
        });

        const response = await get(`/api/v1/media/albums?${params.toString()}`);
        if (isCancelled) {
          return;
        }

        const rawItems = Array.isArray(response?.items) ? response.items : [];
        const normalizedAlbums = rawItems
          .map((album) => {
            const payload = album?.album || album?.data?.album || album;
            const id = payload?.id || payload?._id || payload?.album_id || payload?.albumId;
            if (!id) {
              return null;
            }

            return {
              id,
              title: payload.title || payload.name || payload.metadata?.slug || t('imageGallery.filters.album.untitled'),
              metadata: payload.metadata || {},
            };
          })
          .filter(Boolean);

        setAlbums(normalizedAlbums);
        albumMediaCacheRef.current.clear();
      } catch (error) {
        if (!isCancelled) {
          console.error('[MobileImageGallery] Failed to load albums', error);
        }
      }
    };

    loadAlbums();

    return () => {
      isCancelled = true;
    };
  }, [get, t]);

  // Ensure selected album exists in the current options
  useEffect(() => {
    if (selectedAlbum === 'all') {
      return;
    }

    const albumExists = albums.some((album) => album.id === selectedAlbum);
    if (!albumExists) {
      setSelectedAlbum('all');
    }
  }, [albums, selectedAlbum]);

  const loadPage = useCallback(async (pageToLoad = 1) => {
    const isInitialLoad = pageToLoad === 1;
    const requestToken = Symbol('gallery-load-request');
    activeRequestRef.current = requestToken;

    setGalleryError(null);

    if (isInitialLoad) {
      setIsLoading(true);
      setIsFetchingMore(false);
      setPagination({ page: 1, totalPages: 1, totalItems: 0 });
      setHasMore(true);
    } else {
      setIsFetchingMore(true);
    }

    try {
      if (selectedAlbum && selectedAlbum !== 'all') {
        let normalizedAlbumMedia = albumMediaCacheRef.current.get(selectedAlbum);

        if (!normalizedAlbumMedia) {
          const albumMediaItems = await fetchAlbumMediaItems({ get, albumId: selectedAlbum });
          normalizedAlbumMedia = albumMediaItems.map((item) => normalizeMediaItem(item)).filter(Boolean);
          albumMediaCacheRef.current.set(selectedAlbum, normalizedAlbumMedia);
        }

        const filteredAlbumMedia = normalizedAlbumMedia.filter((item) => {
          const matchesSubtype = selectedType === 'all' || item.type === selectedType;
          const matchesMediaType = selectedMediaType === 'all' || item.mediaType === selectedMediaType;
          return matchesSubtype && matchesMediaType;
        });

        const totalPages = Math.ceil(filteredAlbumMedia.length / DEFAULT_GALLERY_PAGE_SIZE) || 1;
        const startIndex = (pageToLoad - 1) * DEFAULT_GALLERY_PAGE_SIZE;
        const endIndex = startIndex + DEFAULT_GALLERY_PAGE_SIZE;
        const pageItems = filteredAlbumMedia.slice(startIndex, endIndex);

        if (activeRequestRef.current !== requestToken) {
          return;
        }

        setMediaItems((prev) => (isInitialLoad ? pageItems : [...prev, ...pageItems]));
        setPagination({
          page: pageToLoad,
          totalPages,
          totalItems: filteredAlbumMedia.length,
        });
        setHasMore(pageToLoad < totalPages);
        return;
      }

      const params = new URLSearchParams({
        page: String(pageToLoad),
        page_size: String(DEFAULT_GALLERY_PAGE_SIZE),
      });

      if (selectedMediaType && selectedMediaType !== 'all') {
        params.set('media_type', selectedMediaType);
      }

      const subtypeValue = getSubtypeQueryValue(selectedType);
      if (subtypeValue) {
        params.set('subtype', subtypeValue);
      }

      const response = await get(`/api/v1/media/files?${params.toString()}`);
      const items = Array.isArray(response?.items) ? response.items : [];
      const normalized = items
        .map((entry) => normalizeMediaItem(entry?.media_file || entry?.media || entry))
        .filter(Boolean);

      if (activeRequestRef.current !== requestToken) {
        return;
      }

      setMediaItems((prev) => (isInitialLoad ? normalized : [...prev, ...normalized]));

      const paginationInfo = response?.pagination || {};
      const totalPages = paginationInfo.total_pages || paginationInfo.totalPages || (() => {
        const totalItems = paginationInfo.total_items || paginationInfo.totalItems;
        if (typeof totalItems === 'number') {
          const pageSizeValue = paginationInfo.page_size || paginationInfo.pageSize || DEFAULT_GALLERY_PAGE_SIZE;
          return Math.max(1, Math.ceil(totalItems / pageSizeValue));
        }
        if (normalized.length < DEFAULT_GALLERY_PAGE_SIZE) {
          return pageToLoad;
        }
        return pageToLoad + 1; // assume there might be another page
      })();

      const totalItems = paginationInfo.total_items || paginationInfo.totalItems || null;

      setPagination({
        page: pageToLoad,
        totalPages,
        totalItems,
      });

      const hasNextPage = (() => {
        if (paginationInfo.total_pages || paginationInfo.totalPages) {
          return pageToLoad < totalPages;
        }
        return normalized.length === DEFAULT_GALLERY_PAGE_SIZE;
      })();

      setHasMore(hasNextPage);
    } catch (error) {
      if (activeRequestRef.current !== requestToken) {
        return;
      }

      console.error('[MobileImageGallery] Failed to load media list', error);
      setGalleryError(error.message || t('imageGallery.states.error.defaultMessage'));
      if (pageToLoad === 1) {
        setMediaItems([]);
      }
      setHasMore(false);
    } finally {
      if (activeRequestRef.current === requestToken) {
        if (isInitialLoad) {
          setIsLoading(false);
        } else {
          setIsFetchingMore(false);
          setIsLoading(false);
        }
      }
    }
  }, [get, normalizeMediaItem, selectedAlbum, selectedMediaType, selectedType, t]);

  useEffect(() => {
    setMediaItems([]);
    setPagination({ page: 1, totalPages: 1, totalItems: 0 });
    setHasMore(true);
    setGalleryError(null);

    loadPage(1);
  }, [selectedAlbum, selectedMediaType, selectedType, loadPage]);

  const filteredMedia = useMemo(() => {
    return mediaItems.filter((item) => {
      const matchesSubtype = selectedType === 'all' || item.type === selectedType;
      const matchesMediaType = selectedMediaType === 'all' || item.mediaType === selectedMediaType;
      return matchesSubtype && matchesMediaType;
    });
  }, [mediaItems, selectedMediaType, selectedType]);

  useEffect(() => {
    let isCancelled = false;
    const candidates = filteredMedia
      .map((item, index) => ({ item, index }))
      .filter(({ item, index }) => (
        item?.mediaType === 'video' && index <= sequentialLoadCursor
      ));

    if (candidates.length === 0) {
      return () => {
        isCancelled = true;
      };
    }

    candidates.forEach(({ item }) => {
      const key = resolveThumbnailKey(item);
      if (!key || videoThumbnails[key] || videoThumbnailsLoading[key]) {
        return;
      }

      setVideoThumbnailsLoading((prev) => ({ ...prev, [key]: true }));
      const sourceUrl = resolveMediaSourceUrl(item);
      if (!sourceUrl) {
        setVideoThumbnailsLoading((prev) => {
          const next = { ...prev };
          delete next[key];
          return next;
        });
        return;
      }

      generateVideoThumbnail(sourceUrl, { width: 240, quality: 0.8 })
        .then((thumbnail) => {
          if (isCancelled || !thumbnail) {
            return;
          }
          setVideoThumbnails((prev) => ({ ...prev, [key]: thumbnail }));
        })
        .catch((error) => {
          if (!isCancelled) {
            console.error('[MobileImageGallery] Failed to generate video thumbnail', error);
          }
        })
        .finally(() => {
          if (isCancelled) {
            return;
          }
          setVideoThumbnailsLoading((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
          });
        });
    });

    return () => {
      isCancelled = true;
    };
  }, [
    filteredMedia,
    resolveMediaSourceUrl,
    resolveThumbnailKey,
    sequentialLoadCursor,
    videoThumbnails,
    videoThumbnailsLoading,
  ]);

  const imageLoadingScope = useMemo(() => {
    return `gallery-${selectedAlbum || 'all'}-${selectedType || 'all'}-${selectedMediaType || 'all'}`;
  }, [selectedAlbum, selectedMediaType, selectedType]);

  const resetSequentialCursor = useCallback(() => {
    setSequentialLoadCursor(Math.max(0, parallelLoadLimit - 1));
  }, [parallelLoadLimit]);

  useEffect(() => {
    resetSequentialCursor();
  }, [resetSequentialCursor]);

  useEffect(() => {
    if (sequentialScopeRef.current !== imageLoadingScope) {
      sequentialScopeRef.current = imageLoadingScope;
      resetSequentialCursor();
    }
  }, [imageLoadingScope, resetSequentialCursor]);

  useEffect(() => {
    if (filteredMedia.length === 0) {
      resetSequentialCursor();
    }
  }, [filteredMedia.length, resetSequentialCursor]);

  const isInitialLoading = isLoading && mediaItems.length === 0;

  const renderLoadingSkeleton = useCallback(() => {
    return Array.from({ length: DEFAULT_GALLERY_PAGE_SIZE }).map((_, index) => (
      <div key={`gallery-skeleton-${index}`} className="image-gallery-item image-gallery-item--skeleton">
        <Skeleton variant="rounded" width="100%" height="100%" shimmer />
      </div>
    ));
  }, []);

  const loadNextPage = useCallback(() => {
    if (isLoading || isFetchingMore || !hasMore) {
      return;
    }

    const nextPage = (pagination.page || 1) + 1;
    loadPage(nextPage);
  }, [hasMore, isFetchingMore, isLoading, loadPage, pagination.page]);

  useEffect(() => {
    const sentinel = loadMoreTriggerRef.current;
    if (!sentinel) {
      return () => {};
    }

    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry?.isIntersecting) {
        loadNextPage();
      }
    }, {
      root: null,
      rootMargin: '250px 0px',
      threshold: 0,
    });

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [loadNextPage]);

  // Handle upload from device
  // Handle image selection
  const clearViewerHideTimer = useCallback(() => {
    if (viewerHideTimerRef.current) {
      clearTimeout(viewerHideTimerRef.current);
      viewerHideTimerRef.current = null;
    }
  }, []);

  const handleViewerOpen = useCallback((image) => {
    if (!image) {
      return;
    }
    clearViewerHideTimer();
    setViewerImage(image);
    setIsViewerVisible(true);
  }, [clearViewerHideTimer]);

  const handleViewerClose = useCallback(() => {
    setIsViewerVisible(false);
  }, []);

  const handleViewerSelect = useCallback(() => {
    if (typeof onImageSelect !== 'function' || !viewerImage) {
      return;
    }
    onImageSelect(viewerImage);
    handleViewerClose();
  }, [handleViewerClose, onImageSelect, viewerImage]);

  const handleViewerBackdropClick = useCallback((event) => {
    if (event.target === event.currentTarget) {
      handleViewerClose();
    }
  }, [handleViewerClose]);

  const handleImageClick = useCallback((image) => {
    handleViewerOpen(image);
  }, [handleViewerOpen]);

  const handleSequentialSlotComplete = useCallback(() => {
    setSequentialLoadCursor((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!viewerImage || isViewerVisible) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      setViewerImage(null);
      viewerHideTimerRef.current = null;
    }, VIEWER_ANIMATION_MS);

    viewerHideTimerRef.current = timeoutId;

    return () => {
      clearTimeout(timeoutId);
      if (viewerHideTimerRef.current === timeoutId) {
        viewerHideTimerRef.current = null;
      }
    };
  }, [isViewerVisible, viewerImage]);

  useEffect(() => {
    return () => {
      clearViewerHideTimer();
    };
  }, [clearViewerHideTimer]);

  useEffect(() => {
    if (!viewerImage) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleViewerClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleViewerClose, viewerImage]);

  // Handle type dropdown change
  const handleTypeChange = useCallback((type) => {
    setSelectedType(type);
    setIsTypeDropdownOpen(false);
  }, []);

  // Handle media type dropdown change
  const handleMediaTypeChange = useCallback((type) => {
    setSelectedMediaType(type);
    setIsMediaDropdownOpen(false);
  }, []);

  // Handle album dropdown change
  const handleAlbumChange = useCallback((album) => {
    setSelectedAlbum(album);
    setIsAlbumDropdownOpen(false);
  }, []);

  const mediaTypeOptions = useMemo(() => ([
    { value: 'all', label: t('imageGallery.filters.mediaType.all') },
    { value: 'image', label: t('imageGallery.filters.mediaType.image') },
    { value: 'video', label: t('imageGallery.filters.mediaType.video') },
  ]), [t]);

  // Type options
  const typeOptions = useMemo(() => ([
    { value: 'all', label: t('imageGallery.filters.type.all') },
    { value: 'uploaded', label: t('imageGallery.filters.type.uploaded') },
    { value: 'generated', label: t('imageGallery.filters.type.generated') },
    { value: 'edited', label: t('imageGallery.filters.type.edited') },
  ]), [t]);

  const albumOptions = useMemo(() => {
    return [
      { value: 'all', label: t('imageGallery.filters.album.all') },
      ...albums.map((album) => ({
        value: album.id,
        label: album.title || t('imageGallery.filters.album.untitled'),
      })),
    ];
  }, [albums, t]);

  const handleSwipeClose = useCallback(() => {
    if (typeof onBack === 'function') {
      onBack();
    }
  }, [onBack]);

  const handleSwipeUpdate = useCallback((state) => {
    if (!state) {
      return;
    }

    if (state.swipeDirection === 'right') {
      setSwipeOffset(Math.max(0, state.swipeOffset));
    } else {
      setSwipeOffset(0);
    }
  }, []);

  const handleSwipeEnd = useCallback(() => {
    setSwipeOffset(0);
  }, []);

  const swipeGesture = useMobileSwipe(
    null,
    handleSwipeClose,
    {
      threshold: 60,
      velocityThreshold: 0.35,
      onSwipeUpdate: handleSwipeUpdate,
      onSwipeEnd: handleSwipeEnd,
      preventDefaultTouchmove: false,
      disabled: typeof onBack !== 'function',
    }
  );

  const {
    onTouchStart: swipeTouchStart,
    onTouchMove: swipeTouchMove,
    onTouchEnd: swipeTouchEnd,
    reset: resetSwipeGesture,
    isDragging: isSwipeDragging,
  } = swipeGesture;

  const handleTouchStart = useCallback((event) => {
    if (!swipeTouchStart) {
      return;
    }

    const touch = event.touches?.[0];
    swipeEdgeBlockRef.current = !touch || touch.clientX > 48;
    if (swipeEdgeBlockRef.current) {
      resetSwipeGesture?.();
      return;
    }

    event.stopPropagation();

    swipeTouchStart(event);
  }, [resetSwipeGesture, swipeTouchStart]);

  const handleTouchMove = useCallback((event) => {
    if (swipeEdgeBlockRef.current || !swipeTouchMove) {
      return;
    }

    event.stopPropagation();

    swipeTouchMove(event);
  }, [swipeTouchMove]);

  const handleTouchEnd = useCallback((event) => {
    if (swipeEdgeBlockRef.current) {
      swipeEdgeBlockRef.current = false;
      setSwipeOffset(0);
      return;
    }

    event.stopPropagation();

    swipeEdgeBlockRef.current = false;
    swipeTouchEnd?.(event);
  }, [swipeTouchEnd]);

  const handleTouchCancel = useCallback((event) => {
    event.stopPropagation();
    swipeEdgeBlockRef.current = false;
    resetSwipeGesture?.();
    setSwipeOffset(0);
  }, [resetSwipeGesture]);

  useEffect(() => {
    overlayRef.current = pageRef.current?.closest('.mobile-image-gallery-overlay') || null;

    return () => {
      if (overlayRef.current) {
        overlayRef.current.classList.remove('mobile-image-gallery-overlay--swiping');
        overlayRef.current.style.removeProperty('--mobile-image-gallery-overlay-opacity');
        overlayRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const overlayNode = overlayRef.current;
    if (!overlayNode) {
      return;
    }

    if (!swipeOffset) {
      overlayNode.classList.remove('mobile-image-gallery-overlay--swiping');
      overlayNode.style.removeProperty('--mobile-image-gallery-overlay-opacity');
      return;
    }

    overlayNode.classList.add('mobile-image-gallery-overlay--swiping');

    const width = overlayNode.offsetWidth || (typeof window !== 'undefined' ? window.innerWidth : 1);
    const normalized = Math.min(1, Math.max(0, swipeOffset / Math.max(1, width)));
    const baseOpacity = 0.92;
    const easedOpacity = Math.max(0, baseOpacity * (1 - normalized));
    overlayNode.style.setProperty('--mobile-image-gallery-overlay-opacity', easedOpacity.toFixed(3));
  }, [swipeOffset]);

  const pageWidth = pageRef.current?.offsetWidth || (typeof window !== 'undefined' ? window.innerWidth : 1);
  const clampedSwipeOffset = swipeOffset ? Math.min(swipeOffset, pageWidth) : 0;

  const swipeStyle = clampedSwipeOffset
    ? {
      transform: `translateX(${clampedSwipeOffset}px)`,
      opacity: 1 - Math.min(0.35, clampedSwipeOffset / Math.max(300, pageWidth * 1.2)),
    }
    : undefined;

  const galleryPageClasses = [
    'image-gallery-page',
    swipeOffset ? 'swipe-back-active' : '',
    isSwipeDragging ? 'swipe-back-dragging' : '',
  ].filter(Boolean).join(' ');
  const isViewerVideo = viewerImage?.mediaType === 'video' || viewerImage?.raw?.media_type === 'video';
  const viewerMediaUrl = viewerImage?.selectedUrl || viewerImage?.displayUrl || viewerImage?.url;
  const viewerTitle = t('imageGallery.viewer.defaultTitle', {
    defaultValue: isViewerVideo ? 'Anteprima video' : 'Anteprima immagine',
  });
  const viewerAlt = t('imageGallery.viewer.defaultAlt', {
    defaultValue: isViewerVideo ? 'Video selezionato' : 'Immagine selezionata',
  });
  const viewerSelectLabel = t('imageGallery.viewer.selectButton', {
    defaultValue: isViewerVideo ? 'Scegli video' : 'Scegli immagine',
  });
  const handleViewerVideoMetadata = useCallback((event) => {
    const video = event.currentTarget;
    if (!video || typeof video.currentTime !== 'number') {
      return;
    }
    const duration = Number.isFinite(video.duration) ? video.duration : 0;
    const targetTime = duration > 0 ? Math.min(0.1, Math.max(0, duration - 0.1)) : 0.1;
    if (Number.isFinite(targetTime) && targetTime > 0 && video.currentTime !== targetTime) {
      try {
        video.currentTime = targetTime;
      } catch (error) {
        // Ignore seek failures for preview frame.
      }
    }
  }, []);

  return (
    <div
      ref={pageRef}
      className={galleryPageClasses}
      style={swipeStyle}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchCancel}
    >
      <div className="image-gallery-content">
        {/* Header */}
        <div className="post-edit-header image-gallery-header">
          <button
            className="post-edit-back-button"
            onClick={onBack}
          >
            <BackIcon size={24} />
          </button>
          <h3>{t('imageGallery.header.title')}</h3>
          <div className="post-edit-header-spacer"></div>
        </div>

        {/* Filter Dropdowns */}
        <div className="image-gallery-filter-dropdowns">
          {/* Type Selector */}
          <div className="image-gallery-type-selector" ref={typeDropdownRef}>
            <button
              className="image-gallery-type-select"
              onClick={() => setIsTypeDropdownOpen(!isTypeDropdownOpen)}
            >
              <span>{typeOptions.find(opt => opt.value === selectedType)?.label || t('imageGallery.filters.type.all')}</span>
              <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            {isTypeDropdownOpen && (
              <div className="image-gallery-dropdown-menu">
                {typeOptions.map(option => (
                  <button
                    key={option.value}
                    className={`image-gallery-dropdown-item ${selectedType === option.value ? 'active' : ''}`}
                    onClick={() => handleTypeChange(option.value)}
                  >
                    {option.label}
                    {selectedType === option.value && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M20 6L9 17L4 12"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Media Type Selector */}
          <div className="image-gallery-media-selector" ref={mediaDropdownRef}>
            <button
              className="image-gallery-media-select"
              onClick={() => setIsMediaDropdownOpen(!isMediaDropdownOpen)}
            >
              <span>{mediaTypeOptions.find(opt => opt.value === selectedMediaType)?.label || t('imageGallery.filters.mediaType.all')}</span>
              <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            {isMediaDropdownOpen && (
              <div className="image-gallery-dropdown-menu">
                {mediaTypeOptions.map(option => (
                  <button
                    key={option.value}
                    className={`image-gallery-dropdown-item ${selectedMediaType === option.value ? 'active' : ''}`}
                    onClick={() => handleMediaTypeChange(option.value)}
                  >
                    {option.label}
                    {selectedMediaType === option.value && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M20 6L9 17L4 12"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Album Selector */}
          <div className="image-gallery-album-selector" ref={albumDropdownRef}>
            <button
              className="image-gallery-album-select"
              onClick={() => setIsAlbumDropdownOpen(!isAlbumDropdownOpen)}
            >
              <span>{albumOptions.find(opt => opt.value === selectedAlbum)?.label || t('imageGallery.filters.album.all')}</span>
              <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
            {isAlbumDropdownOpen && (
              <div className="image-gallery-dropdown-menu">
                {albumOptions.map(option => (
                  <button
                    key={option.value}
                    className={`image-gallery-dropdown-item ${selectedAlbum === option.value ? 'active' : ''}`}
                    onClick={() => handleAlbumChange(option.value)}
                  >
                    {option.label}
                    {selectedAlbum === option.value && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M20 6L9 17L4 12"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Images Grid */}
        <div className={`image-gallery-grid ${isInitialLoading ? 'image-gallery-grid--loading' : ''}`}>
          {galleryError ? (
            <div className="image-gallery-empty">
              <p>{t('imageGallery.states.error.title')}</p>
              <span>{galleryError}</span>
            </div>
          ) : filteredMedia.length > 0 ? (
            filteredMedia.map((image, index) => (
              <GalleryThumbnail
                key={image.id}
                image={image}
                onSelect={handleImageClick}
                getImageLoadingProps={getImageLoadingProps}
                imageLoadingScope={imageLoadingScope}
                shouldLoadImage={index <= sequentialLoadCursor}
                onSequentialLoadComplete={handleSequentialSlotComplete}
                thumbnailUrl={resolveThumbnailKey(image) ? videoThumbnails[resolveThumbnailKey(image)] : null}
              />
            ))
          ) : isInitialLoading ? (
            renderLoadingSkeleton()
          ) : (
            <div className="image-gallery-empty">
              <p>{t('imageGallery.states.empty.title')}</p>
              <span>{t('imageGallery.states.empty.subtitle')}</span>
            </div>
          )}
        </div>

        <div ref={loadMoreTriggerRef} className="image-gallery-load-more-sentinel">
          {isFetchingMore && !isLoading && hasMore && (
            <div className="image-gallery-loading-more">
              <span>{t('imageGallery.states.loadingMore')}</span>
            </div>
          )}
        </div>
      </div>
      {(viewerImage || isViewerVisible) && (
        <div
          className={`image-gallery-viewer-overlay ${isViewerVisible ? 'visible' : ''}`}
          role="dialog"
          aria-modal="true"
          data-mobile-swipe-blocker="true"
          onClick={handleViewerBackdropClick}
        >
          <div
            className={`image-gallery-viewer-modal ${isViewerVisible ? 'visible' : ''}`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="image-gallery-viewer-header">
              <h4>{viewerImage?.name || viewerTitle}</h4>
              <button
                type="button"
                className="image-gallery-viewer-close"
                onClick={handleViewerClose}
                aria-label={t('imageGallery.viewer.closeButton', { defaultValue: 'Chiudi anteprima' })}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
            <div className="image-gallery-viewer-body">
              {viewerImage && (isViewerVideo ? (
                <video
                  src={viewerMediaUrl}
                  className="image-gallery-viewer-img"
                  controls
                  preload="auto"
                  playsInline
                  poster={videoPlaceholder}
                  onLoadedMetadata={handleViewerVideoMetadata}
                />
              ) : (
                <img
                  src={viewerMediaUrl}
                  alt={viewerImage.name || viewerAlt}
                  className="image-gallery-viewer-img"
                />
              ))}
            </div>
            {showViewerSelectButton && (
              <div className="image-gallery-viewer-footer">
                <button
                  type="button"
                  className="image-gallery-viewer-select"
                  onClick={handleViewerSelect}
                >
                  {viewerSelectLabel}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageGalleryPage;
